import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Skeleton,
  SvgIcon,
  Collapse,
  Popper,
  ClickAwayListener,
  Paper,
} from '@mui/material'
import {
  MicNoneOutlined,
  Send as SendIcon,
  ChevronLeft,
  ExpandMore,
  PersonOutline,
  BarChartOutlined,
  PeopleOutlined,
  PaymentOutlined,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useSupportAssistant } from '../support-assistant.hooks'
import { useTypewriter } from '@hapana/shared/web/hooks/use-typewriter.hook'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_VARIANTS,
  BUTTON_THEME,
  BUTTON_SIZE,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { ChatAction } from '@hapana/shared/schemas/support'
import { ComponentSpecRenderer } from './component-spec-renderer'
import { markdownToHtml } from '@hapana/shared/web/utils/markdown-to-html.util'
import { sanitizeHtml } from '../../../utils/html-sanitizer'
import { RecentChats } from './recent-chats'
import { ChatHistoryList } from './chat-history-list'
import { MessageFeedback } from './message-feedback'
import { resolveThinkingStepMessage } from '../constants/thinking-step-messages'
import * as styles from './assistant-tab.styles'

// Hapana AI Star Icon Component
const HapanaAiStarIcon: React.FC<{ sx?: any }> = ({ sx }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      width: '16px',
      height: '16px',
      display: 'block',
      flexShrink: 0,
      ...sx,
    }}
  >
    <path
      d="M7.34496 1.87605C7.37353 1.72312 7.45468 1.58499 7.57436 1.4856C7.69404 1.3862 7.84472 1.33179 8.00029 1.33179C8.15587 1.33179 8.30655 1.3862 8.42623 1.4856C8.54591 1.58499 8.62706 1.72312 8.65563 1.87605L9.35629 5.58138C9.40606 5.84482 9.53408 6.08713 9.72365 6.2767C9.91322 6.46627 10.1555 6.59429 10.419 6.64405L14.1243 7.34472C14.2772 7.37328 14.4154 7.45443 14.5147 7.57412C14.6141 7.6938 14.6686 7.84447 14.6686 8.00005C14.6686 8.15563 14.6141 8.3063 14.5147 8.42599C14.4154 8.54567 14.2772 8.62682 14.1243 8.65538L10.419 9.35605C10.1555 9.40581 9.91322 9.53383 9.72365 9.7234C9.53408 9.91297 9.40606 10.1553 9.35629 10.4187L8.65563 14.1241C8.62706 14.277 8.54591 14.4151 8.42623 14.5145C8.30655 14.6139 8.15587 14.6683 8.00029 14.6683C7.84472 14.6683 7.69404 14.6139 7.57436 14.5145C7.45468 14.4151 7.37353 14.277 7.34496 14.1241L6.64429 10.4187C6.59453 10.1553 6.46651 9.91297 6.27694 9.7234C6.08737 9.53383 5.84506 9.40581 5.58163 9.35605L1.87629 8.65538C1.72336 8.62682 1.58524 8.54567 1.48584 8.42599C1.38644 8.3063 1.33203 8.15563 1.33203 8.00005C1.33203 7.84447 1.38644 7.6938 1.48584 7.57412C1.58524 7.45443 1.72336 7.37328 1.87629 7.34472L5.58163 6.64405C5.84506 6.59429 6.08737 6.46627 6.27694 6.2767C6.46651 6.08713 6.59453 5.84482 6.64429 5.58138L7.34496 1.87605Z"
      stroke="currentColor"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.333 1.33325V3.99992"
      stroke="currentColor"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.6667 2.66675H12"
      stroke="currentColor"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.66634 14.6667C3.40272 14.6667 3.99967 14.0697 3.99967 13.3333C3.99967 12.597 3.40272 12 2.66634 12C1.92996 12 1.33301 12.597 1.33301 13.3333C1.33301 14.0697 1.92996 14.6667 2.66634 14.6667Z"
      stroke="currentColor"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Link Icon Component
const LinkIcon: React.FC = () => (
  <SvgIcon viewBox="0 0 24 24" sx={styles.linkIconSx}>
    <path
      d="M9.99984 17.5501L8.22984 19.2701C7.76571 19.7342 7.13621 19.995 6.47984 19.995C5.82346 19.995 5.19397 19.7342 4.72984 19.2701C4.26571 18.806 4.00496 18.1765 4.00496 17.5201C4.00496 16.8637 4.26571 16.2342 4.72984 15.7701L9.26984 11.2201C9.71545 10.773 10.3159 10.5144 10.9469 10.4976C11.5778 10.4809 12.1911 10.7073 12.6598 11.1301L12.7798 11.2301C12.9695 11.4157 13.2251 11.5185 13.4904 11.5157C13.7558 11.5128 14.0092 11.4047 14.1948 11.2151C14.3805 11.0255 14.4832 10.7699 14.4804 10.5045C14.4776 10.2391 14.3695 9.98575 14.1798 9.80009C14.1234 9.72712 14.0633 9.65703 13.9998 9.59009C13.1462 8.84741 12.0422 8.45694 10.9114 8.49779C9.78064 8.53864 8.70767 9.00775 7.90984 9.81009L3.30984 14.3601C2.52819 15.2109 2.10546 16.3308 2.12991 17.4858C2.15436 18.6409 2.62412 19.7419 3.44107 20.5589C4.25802 21.3758 5.359 21.8456 6.51408 21.87C7.66917 21.8945 8.78904 21.4717 9.63984 20.6901L11.3698 19.0001C11.5407 18.8137 11.6346 18.5696 11.6326 18.3168C11.6307 18.064 11.5332 17.8214 11.3595 17.6376C11.1859 17.4539 10.9492 17.3427 10.6969 17.3264C10.4446 17.3102 10.1956 17.3901 9.99984 17.5501ZM20.6898 3.31009C19.8486 2.4741 18.7108 2.00488 17.5248 2.00488C16.3389 2.00488 15.2011 2.4741 14.3598 3.31009L12.6298 5.00009C12.459 5.18644 12.3651 5.43057 12.367 5.68336C12.3689 5.93615 12.4665 6.17883 12.6401 6.36258C12.8137 6.54633 13.0505 6.65751 13.3028 6.67375C13.555 6.68998 13.8041 6.61007 13.9998 6.45009L15.7298 4.73009C16.194 4.26597 16.8235 4.00522 17.4798 4.00522C18.1362 4.00522 18.7657 4.26597 19.2298 4.73009C19.694 5.19422 19.9547 5.82372 19.9547 6.48009C19.9547 7.13647 19.694 7.76596 19.2298 8.23009L14.6898 12.7801C14.2442 13.2272 13.6438 13.4858 13.0128 13.5026C12.3818 13.5193 11.7685 13.2929 11.2998 12.8701L11.1798 12.7701C10.9902 12.5844 10.7346 12.4817 10.4692 12.4845C10.2039 12.4874 9.95049 12.5955 9.76484 12.7851C9.57919 12.9747 9.47647 13.2303 9.47928 13.4957C9.48209 13.7611 9.59021 14.0144 9.77984 14.2001C9.85247 14.2744 9.92926 14.3445 10.0098 14.4101C10.8645 15.1505 11.9681 15.5395 13.0981 15.4987C14.2282 15.4579 15.3008 14.9903 16.0998 14.1901L20.6498 9.64009C21.4912 8.80417 21.9676 7.66932 21.9751 6.48334C21.9826 5.29736 21.5205 4.15659 20.6898 3.31009Z"
      fill="currentColor"
    />
  </SvgIcon>
)

/** Format an ISO/UTC timestamp to the viewer's local time (e.g. "09:51") */
function formatLocalTime(isoString: string): string {
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
}

const convertMarkdownLinksInHTML = (html: string): string => {
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
  return html.replace(markdownLinkRegex, (_match, text, url) => {
    return `<a href="${url}">${text}</a>`
  })
}

/** Strip Gemini tool-calling artifacts that may leak into streamed text.
 *  Uses \b word boundary because Gemini sometimes omits the closing bracket. */
const SAFE_HTML_TAGS =
  /^(p|a|b|i|u|em|strong|br|hr|ul|ol|li|h[1-6]|code|pre|blockquote|table|thead|tbody|tr|th|td|div|span|img|sup|sub|del|s|small)$/i

const stripGeminiArtifacts = (text: string): string => {
  let result = text
    .replace(/print\(defaultapi\.[\s\S]*?(\)\)|$)/gi, '')
    .replace(/```(?:json)?\s*\{\s*"components"\s*:[\s\S]*?```/gi, '')
    .trim()
  result = result.replace(/<\/?([a-z_]\w*)\b[^>]*>[\s\S]*?(<\/\1>|$)/gi, (match, tagName) => {
    return SAFE_HTML_TAGS.test(tagName) ? match : ''
  })
  result = result.trim()
  const partialTag = result.match(/<\/?\w*$/)
  if (partialTag) {
    result = result.slice(0, partialTag.index).trim()
  }
  return result
}

const StreamingMessageContent: React.FC<{ text: string }> = ({ text }) => {
  const displayed = useTypewriter({
    text: stripGeminiArtifacts(text),
    isActive: true,
    charsPerFrame: 3,
  })
  return (
    <Box component="span">
      <Box
        component="span"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(markdownToHtml(displayed)) }}
        sx={styles.streamingContentSx}
      />
      <Box component="span" sx={styles.blinkingCursorSx} />
    </Box>
  )
}

// Fade-in/out transition for thinking step text changes
const ThinkingStepText: React.FC<{ stepKey: string }> = ({ stepKey }) => {
  const { t } = useTranslation('core')
  // Resolve key to a random message once per key change
  const [displayed, setDisplayed] = useState(() => resolveThinkingStepMessage(stepKey, t))
  const [visible, setVisible] = useState(true)
  const prevKeyRef = useRef(stepKey)

  useEffect(() => {
    if (stepKey === prevKeyRef.current) return
    prevKeyRef.current = stepKey
    // Fade out, swap text, fade in
    setVisible(false)
    const timer = setTimeout(() => {
      setDisplayed(resolveThinkingStepMessage(stepKey, t))
      setVisible(true)
    }, 200)
    return () => clearTimeout(timer)
  }, [stepKey, t])

  return (
    <Typography variant="body2" sx={styles.thinkingStepTextSx(visible)}>
      {displayed}
    </Typography>
  )
}

// Single-line live preview of thought text with fade transition
const ThinkingPreviewText: React.FC<{ text: string }> = ({ text }) => {
  const { t } = useTranslation('core')
  // Take the last non-empty line as the preview
  const lastLine = text.trim().split('\n').filter(Boolean).pop() || ''
  const [displayed, setDisplayed] = useState(lastLine)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (lastLine === displayed) return
    setVisible(false)
    const timer = setTimeout(() => {
      setDisplayed(lastLine)
      setVisible(true)
    }, 200)
    return () => clearTimeout(timer)
  }, [lastLine, displayed])

  if (!displayed) return null

  return (
    <Typography variant="body2" sx={styles.thinkingPreviewTextSx(visible)}>
      {t('supportAssistant.thinking')} {stripMarkdown(displayed)}
    </Typography>
  )
}

/** Strip markdown formatting to plain text */
function stripMarkdown(text: string): string {
  return text
    .replace(/#{1,6}\s+/g, '') // headings
    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
    .replace(/\*(.+?)\*/g, '$1') // italic
    .replace(/__(.+?)__/g, '$1') // bold alt
    .replace(/_(.+?)_/g, '$1') // italic alt
    .replace(/`{1,3}[^`]*`{1,3}/g, (m) => m.replace(/`/g, '')) // code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/^[-*+]\s+/gm, '') // list items
    .replace(/^\d+\.\s+/gm, '') // numbered lists
    .replace(/^>\s+/gm, '') // blockquotes
}

// Collapsible thought text — plain text label, expands on click
const ThoughtAccordion: React.FC<{ thoughtText: string }> = ({ thoughtText }) => {
  const [expanded, setExpanded] = useState(false)
  const { t } = useTranslation('core')
  return (
    <Box>
      <Box onClick={() => setExpanded(!expanded)} sx={styles.thoughtToggleSx}>
        <ExpandMore sx={styles.thoughtExpandIconSx(expanded)} />
        <Typography className="thought-label" variant="caption" sx={styles.thoughtLabelSx}>
          {t('supportAssistant.thought', 'Thought')}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Typography variant="body2" sx={styles.thoughtContentSx}>
          {stripMarkdown(thoughtText)}
        </Typography>
      </Collapse>
    </Box>
  )
}

export function AssistantTab({ fullWidth = false }: { fullWidth?: boolean }) {
  const {
    isOpen,
    messages,
    sendMessage,
    isStreaming,
    thinkingStep,
    thoughtText,
    isLoadingChat,
    setIsOpen,
    scopeLevel,
    pathname,
    userRole,
    currentView,
    setCurrentView,
    chatId,
    selectedChatId,
    selectedChatTitle,
    loadChat,
    backToNewChat,
    setActiveTab,
    setPrefillDescription,
    setPrefillIssueType,
    openDocsArticle,
  } = useSupportAssistant()
  const [text, setText] = useState('')
  const messageInputRef = useRef<HTMLInputElement>(null)
  const [actionedMessageIds, setActionedMessageIds] = useState<Set<string>>(new Set())
  // Persist thought text per message so it survives after streaming ends
  const [thoughtByMessageId, setThoughtByMessageId] = useState<Record<string, string>>({})
  const { t } = useTranslation('core')
  const navigate = useNavigate()
  const messagesContainerRef = React.useRef<HTMLDivElement>(null)

  // Capture thought text and associate it with the current streaming message
  useEffect(() => {
    if (thoughtText && messages.length > 0) {
      const lastMsg = messages[messages.length - 1]
      if (lastMsg && lastMsg.role === 'assistant') {
        setThoughtByMessageId((prev) => ({ ...prev, [lastMsg.id]: thoughtText }))
      }
    }
  }, [thoughtText, messages])

  // Navigate to support tab with prefilled description from the conversation
  const handleEmailSupport = (assistantMessage: (typeof messages)[number], issueType?: string) => {
    // Find the user message that preceded this assistant response
    const msgIndex = messages.indexOf(assistantMessage)
    const userMsg = messages
      .slice(0, msgIndex)
      .reverse()
      .find((m) => m.role === 'user')

    // Only prefill with the user's message — bot response is covered by "include chat history" toggle
    setPrefillDescription(userMsg?.content || '')
    if (issueType) {
      setPrefillIssueType(issueType)
    }
    setActiveTab('support')
  }

  const actionHandlers: Record<string, (el: HTMLElement, msg: (typeof messages)[number]) => void> =
    {
      [ChatAction.ShowMoreAudit]: () => sendMessage('show more'),
      [ChatAction.EmailSupport]: (el, m) =>
        handleEmailSupport(m, el.getAttribute('data-issue-type') || undefined),
      [ChatAction.ViewArticle]: (el) => {
        const slug = el.getAttribute('data-kb-slug')
        const title = el.textContent || ''
        if (slug) {
          openDocsArticle(slug, title)
        }
      },
      [ChatAction.ConfirmAction]: (el, msg) => {
        const actionId = el.getAttribute('data-action-id')
        const params = el.getAttribute('data-params')
        if (actionId && params) {
          void sendMessage(`__action_confirm:${actionId}:${params}`, 'Confirm action')
          setActionedMessageIds((prev) => new Set([...prev, msg.id]))
        }
      },
      [ChatAction.CancelAction]: (el, msg) => {
        const actionId = el.getAttribute('data-action-id')
        if (actionId) {
          void sendMessage(`__action_cancel:${actionId}`, 'Cancel action')
          setActionedMessageIds((prev) => new Set([...prev, msg.id]))
        }
      },
      [ChatAction.SubmitInput]: (el) => {
        const contextKey = el.getAttribute('data-context-key')
        const useInput = el.getAttribute('data-use-input')
        let value = el.getAttribute('data-value')
        const label = el.textContent?.trim()

        // For date inputs: find the sibling input element and read its value
        if (useInput === 'true' && el.parentElement) {
          const inputEl = el.parentElement.querySelector('input') as HTMLInputElement | null
          value = inputEl?.value || ''
        }

        if (contextKey && value) {
          void sendMessage(`__input_submit:${contextKey}:${value}`, label || value)
        }
      },
      [ChatAction.SubmitInputGroup]: (el) => {
        const contextKeys = el.getAttribute('data-context-keys')?.split(',') || []
        const container = el.parentElement
        if (!container || contextKeys.length === 0) return

        const pairs: string[] = []
        const labels: string[] = []
        for (const key of contextKeys) {
          const input = container.querySelector(
            `input[data-context-key="${key}"]`
          ) as HTMLInputElement | null
          if (input?.value) {
            pairs.push(`${key}:${input.value}`)
            labels.push(input.value)
          }
        }

        if (pairs.length > 0) {
          void sendMessage(`__inputs_submit:${pairs.join('|')}`, labels.join(' – '))
        }
      },
      [ChatAction.ChatAboutAction]: (_el, msg) => {
        // Just focus the text input — user types their question normally
        setActionedMessageIds((prev) => new Set([...prev, msg.id]))
      },
    }

  const navigateHref = (href: string, scope?: string) => {
    if (href.startsWith('/')) {
      const prefix = scope === 'brand' ? '/c' : pathname.match(/^(\/s\/[^/]+)/)?.[1] || ''
      navigate(`${prefix}${href}`)
    } else if (href.startsWith(window.location.origin)) {
      navigate(href.slice(window.location.origin.length) || '/')
    } else if (href.startsWith('http')) {
      window.open(href, '_blank', 'noopener,noreferrer')
    }
  }

  const createMessageClickHandler =
    (msg: (typeof messages)[number]) => (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement

      // Walk up to find an interactive element (anchor or button with data-action)
      let el = target
      while (
        el &&
        el.tagName !== 'A' &&
        !(el.tagName === 'BUTTON' && el.hasAttribute('data-action')) &&
        el !== e.currentTarget
      ) {
        el = el.parentElement as HTMLElement
      }
      if (!el || el === e.currentTarget) return

      e.preventDefault()
      const action = el.getAttribute('data-action')
      if (action) {
        actionHandlers[action]?.(el, msg)
        return
      }
      // Only navigate for anchor elements
      if (el.tagName === 'A') {
        const href = el.getAttribute('href')
        if (href) {
          navigateHref(href, el.getAttribute('data-scope') || undefined)
        }
      }
    }

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior,
      })
    }
  }, [])

  // Auto-scroll to latest message when new messages arrive
  useEffect(() => {
    scrollToBottom()
  }, [messages.length, scrollToBottom])

  // Scroll to bottom when chat finishes loading
  useEffect(() => {
    if (!isLoadingChat && messages.length > 0) {
      scrollToBottom()
    }
  }, [isLoadingChat, messages.length, scrollToBottom])

  // Scroll to bottom periodically while streaming so the latest content stays visible
  useEffect(() => {
    if (!isStreaming) return
    const interval = setInterval(() => scrollToBottom(), 300)
    return () => clearInterval(interval)
  }, [isStreaming, scrollToBottom])

  // Scroll to bottom when streaming finishes (response complete)
  useEffect(() => {
    if (!isStreaming && messages.length > 0) {
      scrollToBottom()
    }
  }, [isStreaming, messages.length, scrollToBottom])

  const handleSend = () => {
    if (!text.trim()) return
    void sendMessage(text.trim())
    setText('')
  }

  // Global Enter key listener: submit when sidebar is open and input has text
  useEffect(() => {
    const handleGlobalEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.shiftKey) return
      // Don't intercept if user is typing in another input/textarea
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (!text.trim() || isStreaming) return
      e.preventDefault()
      handleSend()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleGlobalEnter)
    }
    return () => document.removeEventListener('keydown', handleGlobalEnter)
  }, [isOpen, text, isStreaming])

  const handleQuickQuestion = (question: string) => {
    void sendMessage(question)
  }

  // Quick action prompt buttons above the input
  const QUICK_ACTIONS = [
    {
      label: t('supportAssistant.quickActions.reports'),
      icon: BarChartOutlined,
      prompts: [
        t('supportAssistant.quickPrompts.customReport'),
        t('supportAssistant.quickPrompts.netRevenue'),
        t('supportAssistant.quickPrompts.stripeSettlement'),
        t('supportAssistant.quickPrompts.memberComparison'),
      ],
    },
    {
      label: t('supportAssistant.quickActions.members'),
      icon: PeopleOutlined,
      prompts: [
        t('supportAssistant.quickPrompts.cancelMember'),
        t('supportAssistant.quickPrompts.pauseMembership'),
      ],
    },
    {
      label: t('supportAssistant.quickActions.payments'),
      icon: PaymentOutlined,
      prompts: [
        t('supportAssistant.quickPrompts.activeDisputes'),
        t('supportAssistant.quickPrompts.activeMemberships'),
      ],
    },
  ]
  const [openAction, setOpenAction] = useState<string | null>(null)
  const actionAnchorRefs = useRef<Record<string, HTMLElement | null>>({})

  const handlePromptClick = (prompt: string) => {
    setText(prompt)
    setOpenAction(null)
    // Focus the message input after inserting the shortcut text
    setTimeout(() => messageInputRef.current?.focus(), 0)
  }

  const handleBackToHistory = () => {
    backToNewChat()
    setCurrentView('history')
  }

  const hasMessages = messages.length > 0

  const getNewChatTitle = () => {
    const firstUserMessage = messages.find((msg) => msg.role === 'user')
    if (firstUserMessage) {
      const content = firstUserMessage.content.trim()
      return content.length > 50 ? `${content.substring(0, 50)}...` : content
    }
    return t('supportAssistant.title')
  }

  const isViewingChat = selectedChatId || (chatId && hasMessages)
  const chatTitle = selectedChatId
    ? selectedChatTitle || t('supportAssistant.previousChat')
    : chatId && hasMessages
      ? getNewChatTitle()
      : t('supportAssistant.title')

  return (
    <Box sx={styles.rootSx}>
      {/* Show different content based on current view */}
      {currentView === 'history' ? (
        <>
          <Box sx={styles.historyHeaderSx(fullWidth)}>
            <Box onClick={() => backToNewChat()} sx={styles.backButtonSx}>
              <ChevronLeft sx={styles.chevronLeftSx} />
              <Typography variant="body2" sx={styles.backButtonLabelSx}>
                {t('supportAssistant.backToAssistant', 'Back to Assistant')}
              </Typography>
            </Box>
          </Box>
          <ChatHistoryList onChatClick={loadChat} />
        </>
      ) : currentView === 'chat' ? (
        <>
          {/* Chat Sub-Header - only show when viewing a specific chat */}
          {isViewingChat && (
            <Box sx={styles.chatSubHeaderSx(fullWidth)}>
              <Box
                onClick={selectedChatId ? handleBackToHistory : backToNewChat}
                sx={styles.chatSubHeaderBackSx}
              >
                <ChevronLeft sx={styles.chevronLeftSx} />
                <Typography variant="body2" sx={styles.chatSubHeaderTitleSx}>
                  {selectedChatId
                    ? t('supportAssistant.backToRecentChats', 'Back to recent chats')
                    : chatTitle}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Recent Chats section (only for new chats without messages) */}
          {/* Keep mounted but hidden to prevent GQty unmount state update warning */}
          <Box sx={styles.recentChatsSx(!!isViewingChat)}>
            <RecentChats onViewAllClick={() => setCurrentView('history')} fullWidth={fullWidth} />
          </Box>

          {/* Messages Area */}
          <Box ref={messagesContainerRef} sx={styles.messagesAreaSx(fullWidth)}>
            {/* Loading skeleton */}
            {isLoadingChat && selectedChatId && (
              <>
                <Box sx={styles.skeletonRowEndSx}>
                  <Box sx={styles.skeletonUserBubbleSx}>
                    <Skeleton
                      variant="rounded"
                      width={200}
                      height={60}
                      sx={styles.skeletonRoundedSx}
                    />
                  </Box>
                </Box>
                <Box sx={styles.skeletonRowStartSx}>
                  <Box sx={styles.skeletonAssistantBubbleSx}>
                    <Skeleton variant="rounded" height={100} sx={styles.skeletonAssistantLineSx} />
                    <Skeleton
                      variant="rounded"
                      width={150}
                      height={20}
                      sx={styles.skeletonRoundedSx}
                    />
                  </Box>
                </Box>
              </>
            )}

            {!hasMessages && !(isLoadingChat && selectedChatId) && (
              <>
                {/* Welcome Message */}
                <Box sx={styles.welcomeBoxSx}>
                  <Box sx={styles.messageHeaderSx}>
                    <Box sx={styles.assistantLabelSx}>
                      <HapanaAiStarIcon />
                      {t('supportAssistant.title')}
                    </Box>
                    <Box component="span" sx={styles.timestampSx}>
                      {formatLocalTime(new Date().toISOString())}
                    </Box>
                  </Box>
                  {t('supportAssistant.welcomeMessage')}
                </Box>
              </>
            )}

            {/* Chat Messages */}
            {messages.map((msg) => {
              const isUser = msg.role === 'user'
              const isAssistant = msg.role === 'assistant'
              return (
                <Box key={msg.id} sx={styles.messageRowSx(isUser)}>
                  <Box sx={styles.messageBubbleSx(isUser)} data-testid="support-message-bubble">
                    {isUser ? (
                      <Box sx={styles.messageHeaderSx}>
                        <Box sx={styles.userLabelSx}>
                          <PersonOutline sx={styles.personOutlineIconSx} />
                          {t('supportAssistant.userTitle')}
                        </Box>
                        {msg.createdAt && (
                          <Box component="span" sx={styles.userTimestampSx}>
                            {formatLocalTime(msg.createdAt)}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={styles.messageHeaderSx}>
                        <Box sx={styles.assistantLabelSx}>
                          <HapanaAiStarIcon />
                          {t('supportAssistant.title')}
                        </Box>
                        {msg.createdAt && (
                          <Box component="span" sx={styles.timestampSx}>
                            {formatLocalTime(msg.createdAt)}
                          </Box>
                        )}
                      </Box>
                    )}
                    {msg.type === 'streaming' ? (
                      <StreamingMessageContent text={msg.content} />
                    ) : msg.html ? (
                      <Box
                        component="div"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(convertMarkdownLinksInHTML(msg.html)),
                        }}
                        onClick={createMessageClickHandler(msg)}
                        sx={styles.actionedMessageSx(actionedMessageIds.has(msg.id))}
                      />
                    ) : (
                      <Box
                        component="div"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(
                            convertMarkdownLinksInHTML(
                              markdownToHtml(stripGeminiArtifacts(msg.content))
                            )
                          ),
                        }}
                        onClick={createMessageClickHandler(msg)}
                        sx={styles.messageAnchorSx}
                      />
                    )}
                    {msg.metadata?.componentSpec && !msg.metadata?.pendingAction && (
                      <ComponentSpecRenderer
                        spec={msg.metadata.componentSpec}
                        sendMessage={sendMessage}
                      />
                    )}
                  </Box>

                  {msg.isSupportTicket && (
                    <Box sx={styles.supportTicketBadgeSx}>
                      {t('supportAssistant.emailedSupport')}
                    </Box>
                  )}

                  {isAssistant && msg.type === 'final' && (
                    <MessageFeedback
                      messageId={msg.id}
                      initialFeedback={msg.isMessageFeedbackPositive}
                      onEmailSupportClick={() => handleEmailSupport(msg)}
                    />
                  )}

                  {/* TODO: Re-enable thought accordion below response bubble
                  {isAssistant && thoughtByMessageId[msg.id] && (
                    <Box sx={{ mt: 0.5, maxWidth: '85%' }}>
                      <ThoughtAccordion thoughtText={thoughtByMessageId[msg.id]} />
                    </Box>
                  )}
                  */}
                </Box>
              )
            })}

            {isStreaming && (
              <Box sx={styles.streamingContainerSx}>
                {/* Action step indicator */}
                <Box sx={styles.streamingIndicatorRowSx}>
                  <HapanaAiStarIcon sx={styles.hapanaPulseIconSx} />
                  <ThinkingStepText stepKey={thinkingStep || 'UNDERSTANDING'} />
                </Box>
                {/* TODO: Re-enable thinking preview row
                {thoughtText && <ThinkingPreviewText text={thoughtText} />}
                */}
              </Box>
            )}
          </Box>

          {/* Quick Action Buttons */}
          <Box sx={styles.quickActionsContainerSx(fullWidth)}>
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon
              const isActive = openAction === action.label
              return (
                <React.Fragment key={action.label}>
                  <Box
                    ref={(el: HTMLElement | null) => {
                      actionAnchorRefs.current[action.label] = el
                    }}
                    onClick={() => setOpenAction(isActive ? null : action.label)}
                    sx={styles.quickActionButtonSx(isActive)}
                  >
                    <Icon sx={styles.quickActionIconSx} />
                    {action.label}
                  </Box>
                  <Popper
                    open={isActive}
                    anchorEl={actionAnchorRefs.current[action.label]}
                    placement="top-start"
                    sx={styles.popperSx}
                    modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                  >
                    <ClickAwayListener onClickAway={() => setOpenAction(null)}>
                      <Paper elevation={3} sx={styles.popperPaperSx}>
                        {action.prompts.map((prompt) => (
                          <Box
                            key={prompt}
                            onClick={() => handlePromptClick(prompt)}
                            sx={styles.popperItemSx}
                          >
                            {prompt}
                          </Box>
                        ))}
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                </React.Fragment>
              )
            })}
          </Box>

          {/* Message Input */}
          <Box sx={styles.messageInputOuterSx(fullWidth)}>
            <Box sx={styles.messageInputBorderSx}>
              <Box sx={styles.messageInputInnerSx}>
                {/* TODO: attachment button — hidden until implemented */}
                {false && (
                  <IconButton size="small" sx={styles.attachmentButtonSx}>
                    <LinkIcon />
                  </IconButton>
                )}

                <TextField
                  fullWidth
                  multiline
                  autoFocus={fullWidth}
                  inputRef={messageInputRef}
                  minRows={2}
                  maxRows={4}
                  placeholder={t('supportAssistant.placeholder')}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSend()
                    }
                  }}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: styles.textFieldInputSx,
                  }}
                  inputProps={{
                    'data-testid': 'support-chat-input',
                  }}
                  disabled={isStreaming}
                />
              </Box>

              <Box sx={styles.inputActionsRowSx}>
                {/* TODO: voice input button — hidden until implemented */}
                {false && (
                  <IconButton size="small" sx={styles.voiceButtonSx}>
                    <MicNoneOutlined fontSize="small" />
                  </IconButton>
                )}

                <Button
                  data-testid="support-send-button"
                  label={t('supportAssistant.send')}
                  onClick={() => {
                    if (text.trim()) {
                      handleSend()
                    }
                  }}
                  disabled={isStreaming}
                  variant={BUTTON_VARIANTS.CONTAINED}
                  theme={BUTTON_THEME.PRIMARY}
                  size={BUTTON_SIZE.SMALL}
                  endIcon={SendIcon}
                  sx={styles.sendButtonSx}
                />
              </Box>
            </Box>
          </Box>
        </>
      ) : currentView === 'ticket' ? (
        <Box sx={styles.ticketViewSx}>
          <SidebarHeader
            title={t('supportAssistant.emailSupport')}
            leftIcon={<ChevronLeft />}
            onLeftIconClick={() => setCurrentView('chat')}
            onClose={() => setIsOpen(false)}
          />
          {/* TicketForm will be rendered by parent */}
        </Box>
      ) : null}
    </Box>
  )
}
